export interface Logger {
  info: (x: string) => void;
  error: (x: string) => void;
}

export enum Locale {
  en = 'English',
  es = 'Spanish',
}

export interface ExternalReference {
  id: string;
  type: string;
}

export enum FinancingDecision {
  applicantNotFound = 'applicantNotFound',
  approved = 'approved',
  creditFrozen = 'creditFrozen',
  canceled = 'canceled',
  approvedWithStipulations = 'approvedWithStipulations',
  declined = 'declined',
  error = 'error',
  expired = 'expired',
  new = 'new',
  pending = 'pending',
  processing = 'processing',
  withdrawn = 'withdrawn',
  notChosen = 'notChosen',
}

export enum UnderwritingNotes {
  applicantNotFound = 'Applicant not found',
  creditIsFrozen = 'Credit is frozen',
  creditScoreTooLow = 'Credit score too low',
  insufficientCreditHistory = 'Insufficient credit history',
  limitedCreditExperience = 'Limited credit experience',
  recentBankruptcy = 'Bankruptcy',
  preApproved = 'Application was pre-approved',
  ssnMismatch = 'SSN Mismatch',
  approvedDespiteBureauFreeze = 'Approved despite bureau freeze',
  other = 'Other: Error, unable to obtain credit report',
}

export const UnderwritingNotesTranslations: Record<UnderwritingNotes, Record<Locale, string>> = {
  [UnderwritingNotes.applicantNotFound]: {
    [Locale.en]: 'Applicant not found',
    [Locale.es]: 'Solicitante no encontrado',
  },
  [UnderwritingNotes.creditIsFrozen]: {
    [Locale.en]: 'Credit is frozen',
    [Locale.es]: 'El crédito está congelado',
  },
  [UnderwritingNotes.creditScoreTooLow]: {
    [Locale.en]: 'Credit score too low',
    [Locale.es]: 'Puntuación de crédito demasiado baja',
  },
  [UnderwritingNotes.insufficientCreditHistory]: {
    [Locale.en]: 'Insufficient credit history',
    [Locale.es]: 'Historial de crédito insuficiente',
  },
  [UnderwritingNotes.limitedCreditExperience]: {
    [Locale.en]: 'Limited credit experience',
    [Locale.es]: 'Experiencia de crédito limitada',
  },
  [UnderwritingNotes.recentBankruptcy]: {
    [Locale.en]: 'Bankruptcy',
    [Locale.es]: 'Bancarrota',
  },
  [UnderwritingNotes.preApproved]: {
    [Locale.en]: 'Application was pre-approved',
    [Locale.es]: 'La solicitud fue preaprobada',
  },
  [UnderwritingNotes.ssnMismatch]: {
    [Locale.en]: 'Applicant not found',
    [Locale.es]: 'Solicitante no encontrado',
  },
  [UnderwritingNotes.approvedDespiteBureauFreeze]: {
    [Locale.en]: 'Approved despite bureau freeze',
    [Locale.es]: 'Aprobado a pesar de bloqueo en buró',
  },
  [UnderwritingNotes.other]: {
    [Locale.en]: 'Other: Error, unable to obtain credit report',
    [Locale.es]: 'Otro: Error, no se puede obtener el informe de crédito',
  },
};

export * from './AccountActivity';
export * from './AccountConditionEvents';
export * from './AccountctlRun';
export * from './Brands';
export * from './AccountExports';
export * from './AccountImport';
export * from './AccountMilestones';
export * from './AccountPayoutEvents';
export * from './AccountRequirements';
export * from './AccountStatusChange';
export * from './AccountSettingsSnapshot';
export * from './AccountTasks';
export * from './AccountTypes';
export * from './ActivationPackage';
export * from './ActivityEvents';
export * from './Adders';
export * from './TextNormalization';
export * from './AdminTasks';
export * from './Aging';
export * from './Application';
export * from './ApprovedVendors';
export * from './ApprovedVendorList';
export * from './AppSettings';
export * from './AssetTypes';
export * from './AsyncMessages';
export * from './Attestment';
export * from './AuthTokenCache';
export * from './Calendar';
export * from './ChecklistEvaluations';
export * from './ChecklistEvaluationRegistry';
export * from './ChecklistEvaluationSystemSize';
export * from './ChecklistEvaluationArrayPitch';
export * from './ChecklistEvaluationSerialValidation';
export * from './ChecklistEvaluationSystemLoss';
export * from './normalizeCountyName';
export * from './Checklists';
export * from './CapBasedByFico';
export * from './matchSerialAgainstAnnexII';
export * from './ChangeOrders';
export * from './ContractDocumentSettings';
export * from './ConcordAccountNotes';
export * from './ConcordDisposition';
export * from './ConcordLogTypes';
export * from './CongaContracts';
export * from './CongaPresigning';
export * from './CongaTestEnvelopes';
export * from './Contracts';
export * from './CookieTypes';
export * from './CreditCheck';
export * from './DcRepricing';
export * from './DispatchServiceEvents';
export * from './DirectPayTypes';
export * from './Disclosures';
export * from './DocumentPullCategories';
export * from './DocumentTypes';
export * from './documentUploadEvents';
export * from './DomesticContentTypes';
export * from './EightyNinetyTypes';
export * from './EmailVerificationList';
export * from './EnergyBackup';
export * from './Entities';
export * from './EquipmentException';
export * from './EquipmentManufacturers';
export * from './Errors';
export * from './Events';
export * from './ExecutionContext';
export * from './FileExtensionWhitelist';
export * from './FinanceProducts';
export * from './FinancialAccount';
export * from './FinancingData';
export * from './Flags';
export * from './GenAI';
export * from './GlideTypes';
export * from './Helpers';
export * from './History';
export * from './InboundPayments';
export * from './InboundPaymentsAccountStatus';
export * from './AssetIncentiveBlocks';
export * from './IncomingInvoices';
export * from './InstallPackage';
export * from './InviteTypes';
export * from './Jobs';
export * from './ListFilters';
export * from './Logs';
export * from './Messages';
export * from './Milestones';
export * from './Modifiers';
export * from './OrganizationMilestones';
export * from './OrganizationPricing';
export * from './Organizations';
export * from './OrganizationSettings';
export * from './OrganizationSettingsV2';
export * from './OrganizationTypes';
export * from './Packages';
export * from './PaymentPlans';
export * from './Payouts/Batches';
export * from './Payouts/Clawbacks';
export * from './Payouts/CsvImport';
export * from './Payouts/Deductions';
export * from './Payouts/Events';
export * from './Payouts/EventUIMappings';
export * from './Payouts/Payees';
export * from './Payouts/Transactions';
export * from './PostActivationPackage';
export * from './PriceSheetMapping';
export * from './PriceSheets';
export * from './PricingSettings';
export * from './ProductCatalog';
export * from './ProductLines';
export * from './ProductPricing';
export * from './ProgramTypes';
export * from './Promos';
export * from './PromptTemplateNames';
export * from './PropertyService';
export * from './PropertyVerificationLogs';
export * from './Queues';
export * from './Quotes';
export * from './ConsumerSolar';
export * from './ReferenceIds';
export * from './RejectionReasons';
export * from './RequirementFlagSettings';
export * from './Salesforce';
export * from './SalesRepTypes';
export * from './PricingPlans';
export * from './Stipulations';
export * from './SystemDesigns';
export * from './Tasks';
export * from './UserTypes';
export * from './Utility';
export * from './ValidateChange';
export * from './Warranties';
export * from './WarrantyContractSkus';
export * from './Webhooks';
export * from './GenAI';
export * from './IdentityVerificationReports';
export * from './HardwareDetails';
export * from './SSE';
export * from './Sundial';
export * from './StandardHoldbackSelfService';
export * from './SundialEligibility';
export * from './DispatchServiceEvents';
export * from './IntercomCleanupLogs';
export * from './IntercomContactRepairLogs';
export * from './OrganizationSyncEvents';
